import React, { useState, useRef, useEffect } from "react";
import { get, post as postRequest, deleteRequest, patchRequest } from "../http/api";
import { useConfirmModal } from "../components/reusable/ConfirmModalContext";
import './PostModal.css'
import PostViewerModal from "./PostViewerModal";
import { useTranslation } from "react-i18next";


export default function PostModal({
    postId,
    onClose,
    scrollToLastComment = false,
    highlightedCommentId
}) {

    const { t } = useTranslation();

    const id_user = sessionStorage.getItem("id_user");

    const { openConfirmModal, closeModal } = useConfirmModal();

    const [post, setPost] = useState(null);

    const commentListRef = useRef(null);

    const [showSentAnimation, setShowSentAnimation] = useState(false);

    const [commentsList, setCommentsList] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(false);

    const [newComment, setNewComment] = useState("");

    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editedCommentText, setEditedCommentText] = useState("");

    const [replyingTo, setReplyingTo] = useState(null);
    const [replyingUser, setReplyingUser] = useState(null);

    const [replyText, setReplyText] = useState("");

    const [expandedReplies, setExpandedReplies] = useState({});


    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showShareModal, setShowShareModal] = useState(false);
    const [publishedCommentId, setPublishedCommentId] = useState(null);
    const [matches, setMatches] = useState([]);
    const [shareError, setShareError] = useState("");





    const formatDate = (dateString) => {
        if (!dateString) return "";

        const now = new Date();
        const postDate = new Date(dateString);

        const diff = Math.floor((now - postDate) / 1000);

        if (diff < 60)
            return t("postModal.date.now");
        if (diff < 3600)
            return t(
                "postModal.date.minutesAgo",
                {
                    count: Math.floor(diff / 60)
                }
            );
        if (diff < 86400)
            return t(
                "postModal.date.hoursAgo",
                {
                    count: Math.floor(diff / 3600)
                }
            );

        return postDate.toLocaleDateString();
    };




    const fetchComments = async () => {

        setCommentsLoading(true);

        try {

            const res = await get(
                `posts/${post.id_post}/comments`
            );

            const comments = Array.isArray(res.data)
                ? res.data
                : [];

            const sorted = comments.sort((a, b) => {

                if (b.is_pinned !== a.is_pinned) {
                    return b.is_pinned - a.is_pinned;
                }

                return new Date(a.created_at)
                    - new Date(b.created_at);

            });

            setCommentsList(sorted);

        } catch (err) {
            console.error(err);
        }

        setCommentsLoading(false);
    };

    useEffect(() => {
        if (post) {
            fetchComments();
        }
    }, [post]);


    const updateCommentLike = (comments, id_comment, liked) => {

        return comments.map(c => {

            if (c.id_comment === id_comment) {

                return {
                    ...c,
                    isLiked: liked ? 1 : 0,
                    likes: liked
                        ? (c.likes || 0) + 1
                        : (c.likes || 0) - 1
                };
            }

            if (c.replies && c.replies.length > 0) {

                return {
                    ...c,
                    replies: updateCommentLike(
                        c.replies,
                        id_comment,
                        liked
                    )
                };
            }

            return c;
        });
    };


    const handleLikeComment = async (id_comment) => {

        try {

            const res = await postRequest(
                `posts/comments/${id_comment}/like`,
                {}
            );

            setCommentsList(prev =>
                updateCommentLike(
                    prev,
                    id_comment,
                    res.data.liked
                )
            );

        } catch (err) {
            console.error(err);
        }
    };


    const handleReply = async (id_comment) => {

        if (!replyText.trim()) return;

        const finalText = replyingUser
            ? `@${replyingUser} ${replyText}`
            : replyText;

        try {

            await postRequest(
                `posts/${post.id_post}/comments`,
                {
                    comment: finalText,
                    parent_id: id_comment
                }
            );

            setReplyText("");
            setReplyingTo(null);
            setReplyingUser(null);

            fetchComments();

        } catch (err) {
            console.error(err);
        }
    };


    const removeCommentAndReplies = (
        comments,
        id_comment
    ) => {

        return comments
            .filter(c => c.id_comment !== id_comment)

            .map(c => {

                if (c.replies?.length > 0) {

                    return {
                        ...c,
                        replies: removeCommentAndReplies(
                            c.replies,
                            id_comment
                        )
                    };
                }

                return c;
            });
    };



    const handleDeleteComment = async (id_comment) => {

        try {

            await deleteRequest(
                `posts/comments/${id_comment}`
            );

            setCommentsList(prev =>
                removeCommentAndReplies(
                    prev,
                    id_comment
                )
            );

        } catch (err) {
            console.error(err);
        }
    };



    const confirmDeleteComment = (id_comment) => {

        openConfirmModal({
            title: t("postModal.deleteComment.title"),
            message: t("postModal.deleteComment.message"),
            confirmText: t("postModal.deleteComment.confirm"),
            cancelText: t("postModal.deleteComment.cancel"),

            onConfirm: async () => {
                await handleDeleteComment(id_comment);
                closeModal();
            }
        });
    };



    const handleEditComment = (c) => {

        setEditingCommentId(c.id_comment);
        setEditedCommentText(c.comment);
    };



    const updateCommentText = (
        comments,
        id_comment,
        newText
    ) => {

        return comments.map(c => {

            if (c.id_comment === id_comment) {

                return {
                    ...c,
                    comment: newText
                };
            }

            if (c.replies?.length > 0) {

                return {
                    ...c,
                    replies: updateCommentText(
                        c.replies,
                        id_comment,
                        newText
                    )
                };
            }

            return c;
        });
    };


    const handleSaveComment = async (id_comment) => {

        try {

            await patchRequest(
                `posts/comments/${id_comment}`,
                {
                    comment: editedCommentText
                }
            );

            setCommentsList(prev =>
                updateCommentText(
                    prev,
                    id_comment,
                    editedCommentText
                )
            );

            setEditingCommentId(null);

        } catch (err) {
            console.error(err);
        }
    };


    const handleLike = async () => {

        try {

            const res = await postRequest(
                `posts/${post.id_post}/like`,
                {}
            );

            setPost(prev => ({
                ...prev,
                isLiked: res.data.liked,
                likes: res.data.liked
                    ? prev.likes + 1
                    : prev.likes - 1
            }));

        } catch (err) {
            console.error(err);
        }
    };


    const handleSharePost = async () => {

        if (selectedUsers.length === 0) {
            setShareError(t("post.selectRecipient"));
            return;
        }

        try {

            for (let userId of selectedUsers) {

                await postRequest(
                    "messages",
                    {
                        receiver_id: userId,
                        type: "post",
                        post_id: post.id_post
                    }
                );

            }

            await postRequest(
                `posts/${post.id_post}/share`,
                {
                    count: selectedUsers.length
                }
            );

            setPost(prev => ({
                ...prev,
                shares:
                    (prev.shares || 0)
                    + selectedUsers.length
            }));

            setShowShareModal(false);
            setSelectedUsers([]);
            setShareError("");
            setShowSentAnimation(true);
            setTimeout(() => {
                setShowSentAnimation(false);
            }, 3000);

        }
        catch (err) {

            console.error(err);

        }

    };

    const closeShareModal = () => {
        setShowShareModal(false);
        setSelectedUsers([]);
        setShareError("");
    };



    useEffect(() => {
        if (showShareModal) {
            fetchMatches();
        }
    }, [showShareModal]);

    const fetchMatches = async () => {
        try {
            const res = await get("matches");
            setMatches(res.data || []);
        } catch (err) {
            console.error(err);
        }
    };




    const handleAddComment = async () => {

        if (!newComment.trim()) return;

        try {

            const res = await postRequest(
                `posts/${post.id_post}/comments`,
                {
                    comment: newComment
                }
            );

            const commentId = res?.data?.id_comment;
            if (commentId) setPublishedCommentId(commentId);
            setNewComment("");

            await fetchComments();
            if (commentId) {
                window.setTimeout(() => setPublishedCommentId(null), 3600);
            }

        } catch (err) {
            console.error(err);
        }
    };


    useEffect(() => {
        fetchPost();
    }, [postId]);

    const fetchPost = async () => {

        try {

            const res = await get(
                `posts/${postId}`
            );

            setPost(res.data);

        } catch (err) {

            if (
                err.message === "POST_NOT_FOUND"
            ) {

                return;
            }

            console.error(err);
        }
    };


    const handleTogglePin = async (id_comment) => {

        try {

            await patchRequest(
                `posts/comments/${id_comment}/pin`
            );

            fetchComments();

        } catch (err) {
            console.error(err);
        }
    };

    const liked = Boolean(post?.isLiked);

    const likes = post?.likes || 0;

    if (!postId) return null;

    return (
        <div
            className="modal-overlay post-page-overlay"
            onClick={onClose}
        >
            <div
                className="post-modal"
                onClick={(e) => e.stopPropagation()}
            >
                {!post ? (
                    <div className="post-modal-skeleton">
                        <div className="pm-sk-header">
                            <div className="pm-sk-avatar skeleton"></div>
                            <div className="pm-sk-name skeleton"></div>
                        </div>

                        <div className="pm-sk-text skeleton"></div>
                        <div className="pm-sk-text short skeleton"></div>

                        <div className="pm-sk-media skeleton"></div>
                    </div>
                ) : (

                    <PostViewerModal
                        post={post}
                        onClose={onClose}
                        highlightedCommentId={publishedCommentId || highlightedCommentId}

                        id_user={id_user}

                        likes={likes}
                        liked={liked}
                        comments={post.comments}
                        shares={post.shares}

                        handleLike={handleLike}

                        commentsLoading={commentsLoading}
                        commentsList={commentsList}

                        commentListRef={commentListRef}

                        formatDate={formatDate}

                        editingCommentId={editingCommentId}
                        editedCommentText={editedCommentText}

                        setEditedCommentText={setEditedCommentText}
                        setEditingCommentId={setEditingCommentId}

                        handleSaveComment={handleSaveComment}

                        confirmDeleteComment={confirmDeleteComment}

                        handleTogglePin={handleTogglePin}

                        handleEditComment={handleEditComment}

                        replyingTo={replyingTo}
                        replyingUser={replyingUser}

                        setReplyingTo={setReplyingTo}
                        setReplyingUser={setReplyingUser}

                        replyText={replyText}
                        setReplyText={setReplyText}

                        handleReply={handleReply}

                        expandedReplies={expandedReplies}
                        setExpandedReplies={setExpandedReplies}

                        handleLikeComment={handleLikeComment}

                        renderReplyItem={() => { }}

                        newComment={newComment}
                        setNewComment={setNewComment}

                        handleAddComment={handleAddComment}

                        scrollToLastComment={scrollToLastComment}
                        handleSharePost={handleSharePost}
                        setShowShareModal={setShowShareModal}
                        matches={matches}
                        setSelectedUsers={setSelectedUsers}
                        shareError={shareError}
                        setShareError={setShareError}
                        closeShareModal={closeShareModal}
                        showShareModal={showShareModal}
                        refreshPosts={fetchPost}
                    />
                )}
            </div>

            {showSentAnimation && (

                <div className="share-success-toast">

                    <div className="share-success-icon">
                        ✓
                    </div>

                    <div>

                        <strong>
                            {t("postModal.share.shared")}
                        </strong>

                        <p>
                            {t("postModal.share.success")}
                        </p>

                    </div>

                </div>

            )}

        </div>
    );
}
